/**
 * Stills of the site, for the metrics chapter: a scored run's tiles, the attempts tile and
 * list, the team notice, the disconnected banner, a leaderboard. Static lookalikes of the real
 * components, with numbers chosen to make the point visible.
 */

import type { ReactNode } from "react";

function Still({ where, children }: { where: string; children: ReactNode }) {
  return (
    <figure className="bg-card min-w-0 rounded-lg border p-4">
      {children}
      <figcaption className="text-muted-foreground mt-3 text-xs">{where}</figcaption>
    </figure>
  );
}

export function AttemptsStill() {
  return (
    <Still where="The challenge page. A reservation nobody connected to is not counted.">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-md border p-3">
          <p className="text-muted-foreground text-xs">Steps</p>
          <p className="mt-1 text-lg font-semibold tabular-nums">2,000</p>
        </div>
        <div className="border-destructive/40 bg-destructive/5 rounded-md border p-3">
          <p className="text-muted-foreground text-xs">Attempts</p>
          <p className="mt-1 text-lg font-semibold tabular-nums">1 left of 3</p>
        </div>
      </div>
      <ul className="mt-3 divide-y text-sm">
        <Row n={1} outcome="Missed" score="0 / 10" />
        <Row n={2} outcome="Partial" score="6 / 10" best />
      </ul>
    </Still>
  );
}

function Row({ n, outcome, score, best }: { n: number; outcome: string; score: string; best?: boolean }) {
  return (
    <li className="flex items-center gap-3 py-2">
      <span className="text-muted-foreground w-5 tabular-nums">{n}</span>
      <span className="flex-1">{outcome}</span>
      {best ? <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[10px] font-medium">Your best</span> : null}
      <span className="tabular-nums">{score}</span>
    </li>
  );
}

export function TeamsStill() {
  return (
    <Still where="The Challenges page, until team changes close.">
      <div className="rounded-md border p-3">
        <p className="text-sm font-medium">You are not on a team yet</p>
        <p className="text-muted-foreground mt-1 text-sm">
          Team changes close in <span className="text-foreground font-medium">6 days</span>. Teams are up
          to 3, and a competition is entered as a team — of one, if you like.
        </p>
        <span className="bg-primary text-primary-foreground mt-3 inline-flex h-7 items-center rounded-md px-2.5 text-xs font-medium">
          Find teammates
        </span>
      </div>
    </Still>
  );
}

export function ClockStill() {
  return (
    <Still where="A run's page, while the connection is down.">
      <div className="border-amber-500/40 bg-amber-500/5 rounded-md border p-3">
        <p className="text-sm font-medium">Disconnected — the run is waiting</p>
        <p className="text-muted-foreground mt-1 text-sm">
          The robot is holding still with 412 steps used; the SDK reconnects by itself. If nobody
          comes back in time, the attempt ends unscored.
        </p>
        <p className="mt-2 text-sm font-medium tabular-nums">4:12 left</p>
      </div>
      <div className="mt-3 flex justify-end">
        <span className="border-destructive/40 text-destructive inline-flex h-7 items-center rounded-md border px-2.5 text-xs font-medium">
          Abort
        </span>
      </div>
    </Still>
  );
}

export function ScoringStill() {
  return (
    <Still where="The leaderboard. Time is shown and never ranks.">
      <table className="w-full text-sm">
        <thead className="text-muted-foreground text-left text-xs">
          <tr>
            <th className="py-1 font-medium">#</th>
            <th className="py-1 font-medium">Team</th>
            <th className="py-1 text-right font-medium">Score</th>
            <th className="py-1 text-right font-medium">SPL</th>
            <th className="py-1 text-right font-medium">Distance</th>
          </tr>
        </thead>
        <tbody className="tabular-nums">
          <tr className="border-t">
            <td className="py-1.5">1</td>
            <td>Team 4</td>
            <td className="text-right">10 / 10</td>
            <td className="text-right">0.91</td>
            <td className="text-right">0.06 m</td>
          </tr>
          <tr className="border-t">
            <td className="py-1.5">2</td>
            <td>Team 1</td>
            <td className="text-right">10 / 10</td>
            <td className="text-right">0.74</td>
            <td className="text-right">0.11 m</td>
          </tr>
          <tr className="border-t text-muted-foreground">
            <td className="py-1.5">3</td>
            <td>Team 2</td>
            <td className="text-right">0 / 10</td>
            <td className="text-right">0</td>
            <td className="text-right">—</td>
          </tr>
        </tbody>
      </table>
    </Still>
  );
}

/** A scored run's tiles, as the run's page shows them. `spl` draws the eye to that one. */
export function ResultStill({ spl = false }: { spl?: boolean }) {
  const tile = (emphasis: boolean) =>
    `rounded-md border p-3 ${emphasis ? "border-primary/50 bg-primary/5" : ""}`;
  return (
    <Still where="A run's page, once it has been scored. Distance and heading appear only for a run that reached the goal.">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className={tile(false)}>
          <p className="text-muted-foreground text-xs">Score</p>
          <p className="mt-1 text-lg font-semibold tabular-nums">6 / 10</p>
          <p className="text-muted-foreground text-xs">distance 3 · heading 2 · spl 1</p>
        </div>
        <div className={tile(!spl)}>
          <p className="text-muted-foreground text-xs">Distance to goal</p>
          <p className="mt-1 text-lg font-semibold tabular-nums">0.0812 m</p>
          <p className="text-muted-foreground text-xs">Within the goal (distance ≤ 0.2 m and heading ≤ 20)</p>
        </div>
        <div className={tile(!spl)}>
          <p className="text-muted-foreground text-xs">Heading error</p>
          <p className="mt-1 text-lg font-semibold tabular-nums">12.3°</p>
          <p className="text-muted-foreground text-xs">off target view 0</p>
        </div>
        <div className={tile(spl)}>
          <p className="text-muted-foreground text-xs">Nav steps</p>
          <p className="mt-1 text-lg font-semibold tabular-nums">
            1,240 <span className="text-muted-foreground text-sm font-normal">· SPL 0.62</span>
          </p>
          <p className="text-muted-foreground text-xs">of a 2,000 step budget · shortest route 769 steps</p>
        </div>
      </div>
    </Still>
  );
}

/** The Scoring card's last line: what the points are, and are not. */
export function GradeStill() {
  return (
    <Still where="The foot of a challenge's Scoring card.">
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-medium">Scoring</p>
        <p className="text-muted-foreground text-xs">10 points</p>
      </div>
      <div className="bg-muted/40 mt-3 h-16 rounded-md border border-dashed" aria-hidden />
      <p className="text-muted-foreground mt-3 text-xs">
        Ranked on points, then SPL, then smaller distance. Time is shown and never ranked.
      </p>
      <p className="text-foreground mt-2 rounded-md border border-primary/40 bg-primary/5 px-2 py-1.5 text-xs">
        These points are the leaderboard&apos;s. Your course grade scales them and may add points
        for the report, the methodology and an autonomous-solution bonus. The numbers the TAs
        announce are the ones that count.
      </p>
    </Still>
  );
}

/** The course's suggested rubric, as a challenge page's Scoring card lays it out. */
export function RubricStill() {
  const block = (title: string, rules: [string, number][]) => (
    <div className="rounded-md border p-3">
      <p className="text-xs font-medium">{title}</p>
      <ul className="mt-1.5 space-y-1 font-mono text-xs">
        {rules.map(([condition, points]) => (
          <li key={condition} className="flex justify-between gap-3">
            <span>{condition}</span>
            <span className="tabular-nums">{points} pts</span>
          </li>
        ))}
      </ul>
    </div>
  );
  return (
    <Still where="A challenge's Scoring card. Within a block the first matching rule pays; the blocks add up.">
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-medium">Scoring</p>
        <p className="text-muted-foreground text-xs">10 points</p>
      </div>
      <p className="mt-2 font-mono text-xs">
        <span className="text-muted-foreground">goal </span>distance ≤ 0.2 m and heading ≤ 20°
        <span className="text-muted-foreground font-sans"> — a run that misses this scores nothing</span>
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {block("Distance", [
          ["≤ 0.1 m", 3],
          ["≤ 0.15 m", 2],
          ["≤ 0.2 m", 1],
        ])}
        {block("Heading", [
          ["≤ 10°", 3],
          ["≤ 15°", 2],
          ["≤ 20°", 1],
        ])}
        {block("SPL", [
          ["≥ 0.95", 4],
          ["≥ 0.85", 3],
          ["≥ 0.75", 2],
          ["≥ 0.5", 1],
        ])}
      </div>
    </Still>
  );
}
